// vendor_picker_view.js — pure HTML-generating module for the vendor picker
// component. No DOM access at import time so it can be tested under Node.js.
//
// The vendor picker is a combobox-style input that searches the local vendor
// library and allows inline creation of new vendors.

/**
 * Escape HTML special characters to prevent XSS.
 * @param {*} v - value to escape
 * @returns {string} HTML-safe string
 */
export function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * Generate HTML for a single result row in the dropdown.
 * @param {object} vendor - vendor object { id, name, phone, serviceTypes, locations }
 * @param {number} index - 0-based index for aria-activedescendant
 * @param {boolean} active - whether this row is currently highlighted
 * @returns {string} HTML string
 */
export function resultRowHtml(vendor, index, active = false) {
  const v = vendor || {}
  const name = v.name || "(unnamed)"
  const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes : []
  const locations = Array.isArray(v.locations) ? v.locations : []

  // Show first service type and first zip if available
  const firstType = serviceTypes.length > 0 ? serviceTypes[0] : ""
  const firstZip = locations.length > 0 && locations[0].zip ? locations[0].zip : ""
  const meta = [firstType, firstZip].filter(Boolean).join(" · ")

  const activeClass = active ? " vendor-picker__result--active" : ""
  const ariaSelected = active ? ' aria-selected="true"' : ""

  return `<div class="vendor-picker__result${activeClass}" role="option" id="vp-opt-${index}" data-vendor-id="${esc(v.id)}"${ariaSelected}>
  <span class="vendor-picker__result-name">${esc(name)}</span>
  ${meta ? `<span class="vendor-picker__result-meta">${esc(meta)}</span>` : ""}
</div>`
}

/**
 * Generate HTML for the "Add new vendor" row at the bottom of results.
 * @param {string} query - the current search query (escaped for display)
 * @param {number} index - 0-based index for aria-activedescendant
 * @param {boolean} active - whether this row is currently highlighted
 * @returns {string} HTML string
 */
export function addRowHtml(query, index, active = false) {
  const activeClass = active ? " vendor-picker__result--active" : ""
  const ariaSelected = active ? ' aria-selected="true"' : ""
  const displayQuery = query.trim() || "new vendor"

  return `<div class="vendor-picker__result vendor-picker__add-row${activeClass}" role="option" id="vp-opt-${index}" data-add="true"${ariaSelected}>
  <span class="vendor-picker__add-icon">&#xFF0B;</span>
  <span class="vendor-picker__add-text">Add "${esc(displayQuery)}" as a new vendor</span>
</div>`
}

/**
 * Generate HTML for the results dropdown list.
 * @param {object} opts
 * @param {Array} opts.results - array of matching vendor objects
 * @param {string} opts.query - the current search query
 * @param {number} opts.activeIndex - index of the currently highlighted item (-1 = none)
 * @param {number} opts.maxResults - maximum number of results to show (default 8)
 * @returns {string} HTML string for the dropdown
 */
export function resultsListHtml({ results, query, activeIndex = -1, maxResults = 8 }) {
  const limitedResults = (results || []).slice(0, maxResults)
  const total = limitedResults.length + 1 // +1 for the add row

  const resultRows = limitedResults.map((v, i) =>
    resultRowHtml(v, i, activeIndex === i)
  ).join("")

  const addRow = addRowHtml(query, limitedResults.length, activeIndex === limitedResults.length)

  return `<div class="vendor-picker__results" role="listbox">${resultRows}${addRow}</div>`
}

/**
 * Generate HTML for the selected vendor display (name + clear button).
 * @param {object} vendor - the selected vendor object
 * @returns {string} HTML string
 */
export function selectedDisplayHtml(vendor) {
  const v = vendor || {}
  const name = v.name || "(unnamed)"

  return `<div class="vendor-picker__selected">
  <span class="vendor-picker__selected-name">${esc(name)}</span>
  <button type="button" class="vendor-picker__clear-btn" title="Clear selection" data-action="vendor-picker#clear">&times;</button>
</div>`
}

/**
 * Generate HTML for the input field when no vendor is selected.
 * @param {string} placeholder - placeholder text for the input
 * @returns {string} HTML string
 */
export function inputHtml(placeholder = "Search vendors...") {
  return `<input type="text" class="vendor-picker__input" placeholder="${esc(placeholder)}" autocomplete="off" role="combobox" aria-expanded="false" aria-autocomplete="list" aria-haspopup="listbox">`
}

/**
 * Generate the full vendor picker HTML structure.
 * @param {object} opts
 * @param {string|null} opts.vendorId - selected vendor id or null/empty
 * @param {object|null} opts.vendor - selected vendor object (when vendorId is set)
 * @param {string} opts.name - form field name for the hidden input
 * @param {string} opts.placeholder - placeholder for the search input
 * @returns {string} HTML string
 */
export function vendorPickerHtml({ vendorId, vendor, name, placeholder = "Search vendors..." }) {
  const hasSelection = vendorId && vendor
  const displayPart = hasSelection ? selectedDisplayHtml(vendor) : inputHtml(placeholder)

  return `<div class="vendor-picker__wrapper">
  ${displayPart}
  <input type="hidden" name="${esc(name)}" value="${esc(vendorId || "")}">
</div>`
}
